import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardWrapper from "../../components/DashboardWrapper";
import CrudTable from "../../components/dashboard/table/CrudTable";
import CrudForm from "../../components/dashboard/form/CrudForm";
import { useCrud } from "../../hooks/useCrud";
import Swal from "sweetalert2";

const generateSlug = (text) =>
  text.toString().toLowerCase().trim().replace(/\s+/g, "-");

export default function Brand() {
  const navigate = useNavigate();

  const {
    data: brands,
    loading,
    errors,
    createItem,
    updateItem,
    deleteItem,
  } = useCrud(
    `${process.env.REACT_APP_API_URL}/backend/brands`,
    ["brand_image"]
  );

  const [editingItem, setEditingItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const allowedFields = useMemo(
    () => ["name", "brand_image", "status"],
    []
  );


  const getFormData = useCallback(
    (item) => {
      if (!item)
        return {
          name: "",
          brand_image: null,
          status: 1,
        };

      return allowedFields.reduce((acc, field) => {
        acc[field] =
          field === "status"
            ? Number(item[field] ?? 1)
            : field === "brand_image"
            ? item[field] || null
            : item[field] || "";
        return acc;
      }, {});
    },
    [allowedFields]
  );

  const hasDuplicate = useCallback(
    (name, slug, id = null) =>
      brands.some(
        (b) =>
          (b.name.toLowerCase() === name.toLowerCase() ||
            b.slug.toLowerCase() === slug.toLowerCase()) &&
          b.id !== id
      ),
    [brands]
  );

  const showSwal = useCallback((title, text, icon) => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: "OK",
      customClass: { confirmButton: "btn btn-primary" },
      buttonsStyling: false,
    });
  }, []);

  const handleSubmit = useCallback(
    async (formData) => { 
      if (!formData.name.trim()) {
        showSwal("Error!", "Brand name is required.", "warning");
        return;
      }

      formData.slug = generateSlug(formData.name);

      if (hasDuplicate(formData.name, formData.slug, editingItem?.id)) {
        showSwal("Duplicate!", "Brand already exists.", "warning");
        return;
      }

      const dataToSend = {};

      allowedFields.forEach((field) => {
        if (field === "brand_image") {
          if (formData.brand_image instanceof File) {
            dataToSend.brand_image = formData.brand_image;
          }
        } else {
          dataToSend[field] = formData[field];
        }
      });

      dataToSend.slug = formData.slug;

      try {
        if (editingItem) {
          await updateItem(editingItem.id, dataToSend);
          showSwal("Updated!", "Brand updated successfully.", "success");
        } else {
          await createItem(dataToSend);
          showSwal("Created!", "Brand created successfully.", "success");
        }

        setModalOpen(false);
        setEditingItem(null);
      } catch (error) {
        showSwal("Error!", error.message || "Something went wrong.", "error");
      }
    },
    [allowedFields, editingItem, updateItem, createItem, hasDuplicate, showSwal]
  );

  const openModal = useCallback((item = null) => {
    setEditingItem(item);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingItem(null);
  }, []);

  const handleDelete = useCallback(
    (id) => {
      Swal.fire({
        title: "Are you sure?",
        text: "This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
         confirmButtonColor: "#0f5f58",
          cancelButtonText: "Cancel",
  cancelButtonColor: "#da4b23",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deleteItem(id);
            showSwal("Deleted!", "Brand deleted.", "success");
          } catch (error) {
            showSwal("Error!", "Delete failed.", "error");
          }
        }
      });
    }, 
    
    [deleteItem, showSwal]
  );

  const handleToggleStatus = useCallback(
    async (item) => {
      try {
        const updatedData = {
          name: item.name,
          slug: item.slug,
          status: item.status ? 0 : 1,
        };

        await updateItem(item.id, updatedData);
        showSwal("Success!", "Status updated.", "success");
      } catch {
        showSwal("Error!", "Failed to update status.", "error");
      }
    },
    [updateItem, showSwal]
  );

  const tableData = useMemo(
    () => brands.map((item, idx) => ({ ...item, sl: idx + 1 })),
    [brands]
  );

  return ( 
    
    <DashboardWrapper>
      <style>{`
  /* =========================
     DOWNLOAD BUTTONS
  ========================= */

  .brandCardHeader {
    padding: 1rem 1.25rem;
  }

  .brandDownloadButtons {
    display: flex;
    gap: 10px !important;
  }

  .brandDownloadButtons button {
    white-space: nowrap;
  }

  /* =========================
     TABLET
  ========================= */

  @media (max-width: 768px) {

    .brandCardHeader {
      flex-wrap: wrap;
      gap: 12px;
    }

    .brandDownloadButtons {
      width: 100%;
      display: flex;
    }

    .brandDownloadButtons button {
      flex: 1;
      width: 50%;
    }
  }

  /* =========================
     MOBILE
  ========================= */

  @media (max-width: 576px) {

    .brandCardHeader {
      padding: 12px;
    }

    .brandCardHeader h4 {
      width: 100%;
      text-align: left;
      font-size: 18px;
      margin-bottom: 4px !important;
    }

    .brandDownloadButtons {
      width: 100%;
      display: flex;
      flex-direction: row;
      gap: 8px !important;
    }

    .brandDownloadButtons button {
      width: 50%;
      flex: 1;
      font-size: 13px;
      padding: 8px 6px;
      white-space: nowrap;
    }
  }

  /* =========================
     SMALL MOBILE
  ========================= */

  @media (max-width: 400px) {

    .brandDownloadButtons {
      gap: 6px !important;
    }

    .brandDownloadButtons button {
      font-size: 12px;
      padding: 7px 4px;
    }
  }
`}</style>
      <div className="container-fluid mt-4">
      <div className="brandTopBar d-flex justify-content-between align-items-center mb-4">
  <button className="btn btn-secondary" onClick={() => navigate(-1)}>
    Back
  </button>

  <button className="btn btn-primary" onClick={() => openModal()}>
    Create Brand
  </button>
</div>

        <div className="card shadow">
       <div className="brandCardHeader d-flex justify-content-between align-items-center">
  <h4 className="mb-0">Brands</h4>

  {/* <div className="brandDownloadButtons d-flex gap-2">
    <button
      className="btn btn-success"
      onClick={() =>
        document.dispatchEvent(new Event("downloadExcel"))
      }
    >
      Download Excel
    </button>

    <button
      className="btn btn-danger"
      onClick={() =>
        document.dispatchEvent(new Event("downloadPDF"))
      }
    >
      Download PDF
    </button>
  </div> */}
</div>

          {/* MODAL */}
          {modalOpen && (
            <div
              className="modal fade show"
              style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
            >
             <div className="modal-dialog modal-dialog-centered brandModalDialog">
                <div className="modal-content">

                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingItem ? "Edit Brand" : "Create Brand"}
                    </h5>
                    <button className="btn-close" onClick={closeModal} />
                  </div>

                  <div className="modal-body">
                    <CrudForm
                      initialData={getFormData(editingItem)}
                      onSubmit={handleSubmit}
                      allowedFields={allowedFields}
                      fileFields={[
                        { name: "brand_image", multiple: false },
                      ]}
                    />
                  </div>

                </div>
              </div>  
            </div>
          )}

          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" />
            </div>
          ) : (
            <div className="card-body">
              <CrudTable
                data={tableData}
                onEdit={openModal}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                columns={["sl", "id", "brand_image", "name", "status"]}
              />
            </div>
          )}
        </div>
      </div>
    </DashboardWrapper>
  );
}